import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Search, ChevronRight, ChevronLeft,
  Store, QrCode, ShoppingBag, CreditCard, Truck, Users,
  BarChart3, Settings, UtensilsCrossed, BookOpen, Palette,
  Clock, MapPin, Smartphone, Shield, Star, Package,
  Monitor, ChefHat, LayoutDashboard, FileText, Megaphone,
  DollarSign, UserCog, MessageSquare, Tag, Percent, Gift,
  Bell, TrendingUp, Receipt, MessageCircle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/* ─── Screen mockup component ─── */
const ScreenMockup = ({ title, children, variant = "desktop" }: { title: string; children: React.ReactNode; variant?: "desktop" | "mobile" }) => (
  <div className={`rounded-xl border border-border overflow-hidden shadow-card ${variant === "mobile" ? "max-w-[280px] mx-auto" : "w-full"}`}>
    <div className="flex items-center gap-1.5 px-3 py-2 bg-muted/60 border-b border-border">
      <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
      <span className="text-[10px] text-muted-foreground ml-2 truncate">{title}</span>
    </div>
    <div className="bg-card p-4">{children}</div>
  </div>
);

const ScreenPlaceholder = ({ sections }: { sections: { label: string; highlight?: boolean }[] }) => (
  <div className="space-y-2">
    {sections.map((s, i) => (
      <div key={i} className={`rounded-lg px-3 py-2 text-xs font-medium ${s.highlight ? "bg-primary/20 text-primary border border-primary/30" : "bg-muted/50 text-muted-foreground"}`}>
        {s.label}
      </div>
    ))}
  </div>
);

/* ─── Tutorial data ─── */
interface TutorialStep {
  title: string;
  description: string;
  screenTitle: string;
  screenSections: { label: string; highlight?: boolean }[];
  screenVariant?: "desktop" | "mobile";
  tip?: string;
}

interface Tutorial {
  id: string;
  icon: React.ElementType;
  category: string;
  title: string;
  description: string;
  difficulty: "Fácil" | "Intermediário" | "Avançado";
  duration: string;
  steps: TutorialStep[];
}

const tutorials: Tutorial[] = [
  {
    id: "cadastro",
    icon: Store,
    category: "Primeiros Passos",
    title: "Criando sua conta e restaurante",
    description: "Aprenda a criar sua conta, configurar seu restaurante e personalizar seu perfil do zero.",
    difficulty: "Fácil",
    duration: "5 min",
    steps: [
      {
        title: "Acesse a página de cadastro",
        description: "Na página inicial, clique em \"Começar Grátis\". Você será redirecionado para o formulário de cadastro com campos de nome, e-mail e senha.",
        screenTitle: "viamenu.app/cadastro",
        screenSections: [
          { label: "📧 Campo: Nome completo" },
          { label: "📧 Campo: E-mail" },
          { label: "🔒 Campo: Senha" },
          { label: "🏪 Campo: Nome do restaurante" },
          { label: "🔗 Campo: Slug (URL personalizada)", highlight: true },
          { label: "🍕 Seletor: Tipo de estabelecimento" },
          { label: "✅ Botão: Criar Conta", highlight: true },
        ],
      },
      {
        title: "Escolha seu slug (URL personalizada)",
        description: "O slug é o endereço único do seu cardápio digital. Ex: viamenu.app/seu-restaurante. Ele é validado em tempo real para garantir que é único e não contém palavras impróprias.",
        screenTitle: "viamenu.app/cadastro",
        screenSections: [
          { label: "🔗 seu-restaurante" },
          { label: "✅ Disponível! URL: viamenu.app/seu-restaurante", highlight: true },
          { label: "⚠️ Validação de caracteres e palavrões em tempo real" },
        ],
        tip: "Escolha um slug curto e fácil de lembrar. Ele será usado no QR Code das mesas."
      },
      {
        title: "Confirme seu e-mail",
        description: "Após o cadastro, você receberá um e-mail de confirmação. Clique no link para ativar sua conta e acessar o painel administrativo.",
        screenTitle: "Caixa de entrada",
        screenSections: [
          { label: "📩 De: ViaMenu <noreply@viamenu.app>" },
          { label: "Assunto: Confirme seu e-mail" },
          { label: "🔗 Botão: Confirmar E-mail", highlight: true },
        ],
      },
      {
        title: "Acesse o Dashboard",
        description: "Após confirmar, faça login e você será direcionado ao Dashboard principal com visão geral de pedidos, faturamento e atalhos rápidos.",
        screenTitle: "viamenu.app/dashboard",
        screenSections: [
          { label: "📊 Cards: Pedidos Hoje | Faturamento | Ticket Médio" },
          { label: "📈 Gráfico de vendas dos últimos 7 dias" },
          { label: "⚡ Atalhos: Cardápio | Pedidos | Configurações", highlight: true },
        ],
      },
    ],
  },
  {
    id: "cardapio",
    icon: UtensilsCrossed,
    category: "Cardápio Digital",
    title: "Gerenciando seu cardápio",
    description: "Cadastre categorias, produtos com fotos, preços, descrições e adicionais personalizáveis.",
    difficulty: "Fácil",
    duration: "8 min",
    steps: [
      {
        title: "Acesse o Gerenciador de Cardápio",
        description: "No menu lateral do Dashboard, clique em \"Cardápio\". Você verá a lista de categorias e produtos cadastrados.",
        screenTitle: "viamenu.app/gerenciar-cardapio",
        screenSections: [
          { label: "➕ Botão: Nova Categoria", highlight: true },
          { label: "📂 Categoria: Pizzas (5 produtos)" },
          { label: "📂 Categoria: Bebidas (8 produtos)" },
          { label: "📂 Categoria: Sobremesas (3 produtos)" },
          { label: "🔀 Arrastar para reordenar" },
        ],
      },
      {
        title: "Crie uma categoria",
        description: "Clique em \"Nova Categoria\". Preencha o nome (ex: Pizzas) e uma descrição opcional. As categorias organizam seu cardápio visualmente.",
        screenTitle: "Nova Categoria",
        screenSections: [
          { label: "📝 Campo: Nome da categoria", highlight: true },
          { label: "📝 Campo: Descrição (opcional)" },
          { label: "🔢 Ordem de exibição: automática" },
          { label: "✅ Botão: Salvar Categoria", highlight: true },
        ],
      },
      {
        title: "Adicione um produto",
        description: "Dentro da categoria, clique em \"Novo Produto\". Preencha nome, preço, descrição e adicione uma foto atrativa.",
        screenTitle: "Novo Produto",
        screenSections: [
          { label: "📷 Upload de foto do produto" },
          { label: "📝 Nome: Pizza Margherita", highlight: true },
          { label: "💰 Preço: R$ 39,90", highlight: true },
          { label: "📝 Descrição: Molho de tomate, mussarela..." },
          { label: "⏱️ Tempo de preparo: 25 min" },
          { label: "⭐ Marcar como destaque" },
        ],
        tip: "Use fotos de alta qualidade e com boa iluminação. Produtos com foto vendem até 30% mais!"
      },
      {
        title: "Configure adicionais",
        description: "Adicione opções extras como tamanhos, bordas, acompanhamentos. Defina preço, quantidade mínima e máxima para cada adicional.",
        screenTitle: "Adicionais do Produto",
        screenSections: [
          { label: "➕ Novo Adicional", highlight: true },
          { label: "🧀 Borda de Catupiry — R$ 8,00" },
          { label: "🥓 Bacon extra — R$ 5,00" },
          { label: "🫒 Azeitonas — R$ 3,00" },
          { label: "⚙️ Mín: 0 | Máx: 3 por item" },
        ],
      },
      {
        title: "Use a descrição com IA",
        description: "Clique no botão de IA ao lado da descrição para gerar automaticamente uma descrição atrativa e profissional para seu produto.",
        screenTitle: "Gerador de Descrição IA",
        screenSections: [
          { label: "🤖 Gerar descrição com IA", highlight: true },
          { label: "📝 \"Pizza artesanal com molho de tomate caseiro...\"" },
          { label: "✅ Usar esta descrição | 🔄 Gerar outra" },
        ],
      },
    ],
  },
  {
    id: "aparencia",
    icon: Palette,
    category: "Personalização",
    title: "Personalizando a aparência do cardápio",
    description: "Customize cores, logo, layout e tipografia do seu cardápio digital para refletir a identidade visual do seu restaurante.",
    difficulty: "Fácil",
    duration: "5 min",
    steps: [
      {
        title: "Acesse Configurações > Aparência",
        description: "No menu lateral, clique em \"Configurações\" e depois na aba \"Aparência\". Aqui você personaliza todo o visual do cardápio.",
        screenTitle: "viamenu.app/configuracoes/aparencia",
        screenSections: [
          { label: "🎨 Cor principal do cardápio", highlight: true },
          { label: "🖼️ Cor de fundo" },
          { label: "📝 Cor do texto" },
          { label: "🃏 Estilo dos cards: Moderno | Clássico | Minimal" },
          { label: "🔤 Fonte: Inter | Poppins | Roboto", highlight: true },
          { label: "📐 Layout: Grade | Lista" },
        ],
      },
      {
        title: "Faça upload do logo e capa",
        description: "Adicione o logo do seu restaurante e uma imagem de capa para o topo do cardápio digital. Recomendamos 400x400px para logo e 1200x400px para capa.",
        screenTitle: "Logo e Capa",
        screenSections: [
          { label: "📷 Upload Logo (400x400px recomendado)", highlight: true },
          { label: "🖼️ Upload Capa (1200x400px recomendado)" },
          { label: "👁️ Pré-visualização em tempo real" },
        ],
      },
      {
        title: "Visualize as mudanças em tempo real",
        description: "Todas as alterações são aplicadas instantaneamente na pré-visualização. Acesse seu cardápio pelo link ou QR Code para ver como ficou no celular.",
        screenTitle: "viamenu.app/seu-restaurante",
        screenVariant: "mobile",
        screenSections: [
          { label: "🏪 Logo + Nome do Restaurante" },
          { label: "🖼️ Imagem de capa" },
          { label: "📂 Categorias com scroll horizontal" },
          { label: "🍕 Cards de produto com foto e preço", highlight: true },
        ],
      },
    ],
  },
  {
    id: "qrcode",
    icon: QrCode,
    category: "Cardápio Digital",
    title: "Gerando QR Codes para as mesas",
    description: "Crie e imprima QR Codes personalizados para cada mesa do seu restaurante.",
    difficulty: "Fácil",
    duration: "3 min",
    steps: [
      {
        title: "Acesse Configurações > QR Codes",
        description: "No menu lateral, vá em \"Configurações\" e clique na aba \"QR Codes\". Defina o número de mesas do seu estabelecimento.",
        screenTitle: "viamenu.app/configuracoes/qr-codes",
        screenSections: [
          { label: "🔢 Número de mesas: 20", highlight: true },
          { label: "➕ / ➖ Ajustar quantidade" },
          { label: "🖨️ Botão: Baixar PDF com todos os QR Codes", highlight: true },
        ],
      },
      {
        title: "Baixe e imprima os QR Codes",
        description: "Clique em \"Baixar PDF\". Cada QR Code direciona o cliente para o cardápio digital com o número da mesa já selecionado automaticamente.",
        screenTitle: "QR Codes gerados",
        screenSections: [
          { label: "📱 Mesa 1 — QR Code + link" },
          { label: "📱 Mesa 2 — QR Code + link" },
          { label: "📱 Mesa 3 — QR Code + link" },
          { label: "💡 Dica: Imprima em adesivo e cole nas mesas", highlight: true },
        ],
        tip: "Use papel adesivo ou acrílico de mesa para fixar os QR Codes. Assim, os clientes podem escanear facilmente."
      },
    ],
  },
  {
    id: "pedidos",
    icon: ShoppingBag,
    category: "Gestão de Pedidos",
    title: "Gerenciando pedidos em tempo real",
    description: "Receba, acompanhe e gerencie todos os pedidos do seu restaurante com atualização em tempo real.",
    difficulty: "Fácil",
    duration: "6 min",
    steps: [
      {
        title: "Acesse a tela de Pedidos",
        description: "No menu lateral, clique em \"Pedidos\". Você verá todos os pedidos organizados por status: Recebidos, Preparando, Prontos e Concluídos.",
        screenTitle: "viamenu.app/pedidos",
        screenSections: [
          { label: "📋 Filtros: Todos | Recebidos | Preparando | Prontos", highlight: true },
          { label: "🟡 Pedido #142 — Mesa 5 — R$ 67,80 — Recebido" },
          { label: "🟠 Pedido #141 — Delivery — R$ 45,00 — Preparando" },
          { label: "🟢 Pedido #140 — Mesa 2 — R$ 92,50 — Pronto" },
          { label: "⚡ Atualização em tempo real" },
        ],
      },
      {
        title: "Detalhes do pedido",
        description: "Clique em um pedido para ver os detalhes: itens, adicionais, observações do cliente, forma de pagamento e endereço de entrega (se delivery).",
        screenTitle: "Pedido #142",
        screenSections: [
          { label: "👤 Cliente: João Silva — Mesa 5" },
          { label: "🍕 1x Pizza Margherita — R$ 39,90" },
          { label: "   ├ Borda de Catupiry (+R$ 8,00)" },
          { label: "🥤 2x Coca-Cola — R$ 19,90" },
          { label: "📝 Obs: Sem cebola" },
          { label: "💳 Pagamento: PIX", highlight: true },
          { label: "💰 Total: R$ 67,80" },
        ],
      },
      {
        title: "Atualize o status do pedido",
        description: "Use os botões de ação para avançar o status: Recebido → Preparando → Pronto → Concluído. O cliente acompanha em tempo real pelo link de rastreamento.",
        screenTitle: "Ações do Pedido",
        screenSections: [
          { label: "▶️ Aceitar Pedido → muda para 'Preparando'", highlight: true },
          { label: "▶️ Marcar como Pronto → notifica o cliente" },
          { label: "▶️ Concluir Pedido → finaliza" },
          { label: "❌ Cancelar Pedido → com motivo obrigatório" },
        ],
      },
    ],
  },
  {
    id: "cozinha",
    icon: ChefHat,
    category: "Gestão de Pedidos",
    title: "Painel da Cozinha (KDS)",
    description: "Configure o painel da cozinha para que sua equipe visualize os pedidos em tempo real sem precisar de comanda de papel.",
    difficulty: "Fácil",
    duration: "4 min",
    steps: [
      {
        title: "Acesse o Painel da Cozinha",
        description: "No menu lateral, clique em \"Cozinha\". O painel mostra os pedidos em formato de kanban otimizado para telas grandes (TV ou tablet).",
        screenTitle: "viamenu.app/cozinha",
        screenSections: [
          { label: "🟡 RECEBIDOS (3)", highlight: true },
          { label: "   Pedido #142 — 2 itens — 3min atrás" },
          { label: "🟠 PREPARANDO (2)" },
          { label: "   Pedido #141 — 1 item — 8min atrás" },
          { label: "🟢 PRONTOS (1)" },
          { label: "   Pedido #140 — 3 itens — pronto há 1min", highlight: true },
        ],
      },
      {
        title: "Interaja com os pedidos",
        description: "Toque no pedido para ver os itens detalhados. Arraste entre as colunas ou use os botões para avançar o status. Alertas sonoros notificam novos pedidos.",
        screenTitle: "Detalhes na cozinha",
        screenSections: [
          { label: "🍕 1x Pizza Margherita (c/ Catupiry)" },
          { label: "📝 SEM CEBOLA", highlight: true },
          { label: "⏱️ Timer: 12:45 (tempo desde recebido)" },
          { label: "✅ Botão: Marcar como Pronto", highlight: true },
        ],
        tip: "Conecte uma TV na cozinha e abra o painel em tela cheia. Sua equipe não precisa de login — apenas acesse a URL."
      },
    ],
  },
  {
    id: "delivery",
    icon: Truck,
    category: "Delivery",
    title: "Configurando o Delivery",
    description: "Configure taxas de entrega, raio de alcance, entregadores e acompanhe as entregas em tempo real.",
    difficulty: "Intermediário",
    duration: "10 min",
    steps: [
      {
        title: "Configure as opções de delivery",
        description: "Vá em Configurações > Delivery. Defina o raio de entrega em km, taxa de entrega fixa e valor mínimo do pedido para delivery.",
        screenTitle: "viamenu.app/configuracoes/delivery",
        screenSections: [
          { label: "📍 Raio de entrega: 8 km", highlight: true },
          { label: "💰 Taxa de entrega: R$ 5,00" },
          { label: "🛒 Pedido mínimo: R$ 25,00" },
          { label: "⏱️ Tempo estimado: 30-50 min" },
        ],
      },
      {
        title: "Cadastre entregadores",
        description: "Na página Delivery, cadastre seus entregadores com nome, telefone, tipo de veículo e valor por entrega.",
        screenTitle: "viamenu.app/delivery",
        screenSections: [
          { label: "➕ Novo Entregador", highlight: true },
          { label: "🏍️ Carlos — Moto — R$ 5,00/entrega — Ativo" },
          { label: "🚗 Ana — Carro — R$ 7,00/entrega — Ativo" },
          { label: "🏍️ Pedro — Moto — R$ 5,00/entrega — Inativo" },
        ],
      },
      {
        title: "Gerencie entregas em andamento",
        description: "Veja todas as entregas ativas, atribua entregadores e acompanhe o status de cada uma.",
        screenTitle: "Entregas Ativas",
        screenSections: [
          { label: "📦 Pedido #142 — Aguardando entregador", highlight: true },
          { label: "   → Atribuir para: Carlos (Moto)" },
          { label: "🏍️ Pedido #141 — Carlos — Em rota" },
          { label: "✅ Pedido #140 — Ana — Entregue há 5min" },
        ],
      },
      {
        title: "Rastreamento pelo cliente",
        description: "Após confirmar o pedido, o cliente recebe um link de acompanhamento com status em tempo real: Preparando → Saiu para entrega → Entregue.",
        screenTitle: "viamenu.app/acompanhar?id=xxx",
        screenVariant: "mobile",
        screenSections: [
          { label: "📍 Acompanhe seu pedido" },
          { label: "✅ Pedido recebido" },
          { label: "✅ Em preparo" },
          { label: "🟡 Saiu para entrega ←", highlight: true },
          { label: "⬜ Entregue" },
          { label: "🏍️ Entregador: Carlos — (11) 99999-0000" },
        ],
      },
    ],
  },
  {
    id: "mesas",
    icon: Monitor,
    category: "Gestão de Mesas",
    title: "Comanda digital e mesas ativas",
    description: "Gerencie mesas abertas, veja itens consumidos em tempo real e feche comandas com facilidade.",
    difficulty: "Intermediário",
    duration: "7 min",
    steps: [
      {
        title: "Visualize as mesas ativas",
        description: "Na página \"Mesas\", veja todas as mesas com comandas abertas, total consumido e tempo desde a abertura.",
        screenTitle: "viamenu.app/mesas",
        screenSections: [
          { label: "🟢 Mesa 1 — R$ 145,00 — 1h20min" },
          { label: "🟢 Mesa 3 — R$ 67,50 — 45min" },
          { label: "🟡 Mesa 5 — R$ 0,00 — Garçom solicitado!", highlight: true },
          { label: "⬜ Mesa 2 — Livre" },
          { label: "⬜ Mesa 4 — Livre" },
        ],
      },
      {
        title: "Veja os itens da comanda",
        description: "Clique na mesa para ver todos os itens consumidos, organizados por sessão (rodada de pedidos). Cada sessão mostra data/hora.",
        screenTitle: "Comanda — Mesa 1",
        screenSections: [
          { label: "📋 Sessão 1 — 19:30" },
          { label: "   🍕 2x Pizza Margherita — R$ 79,80" },
          { label: "   🥤 4x Coca-Cola — R$ 39,60" },
          { label: "📋 Sessão 2 — 20:15" },
          { label: "   🍰 1x Petit Gateau — R$ 25,60" },
          { label: "💰 Total: R$ 145,00", highlight: true },
          { label: "🔒 Botão: Fechar Comanda", highlight: true },
        ],
      },
      {
        title: "Feche a comanda",
        description: "Ao fechar a comanda, selecione a forma de pagamento. O sistema gera automaticamente o pedido finalizado e libera a mesa.",
        screenTitle: "Fechar Comanda",
        screenSections: [
          { label: "💰 Total: R$ 145,00" },
          { label: "💳 Forma de pagamento:", highlight: true },
          { label: "   PIX | Cartão Crédito | Cartão Débito | Dinheiro" },
          { label: "✅ Confirmar Fechamento", highlight: true },
        ],
      },
    ],
  },
  {
    id: "pagamentos",
    icon: CreditCard,
    category: "Pagamentos",
    title: "Configurando formas de pagamento",
    description: "Integre PIX, cartões, MercadoPago e gerencie todas as formas de pagamento do seu restaurante.",
    difficulty: "Intermediário",
    duration: "6 min",
    steps: [
      {
        title: "Acesse Configurações > Pagamento",
        description: "No menu lateral, vá em \"Configurações\" e clique na aba \"Pagamento\". Aqui você configura todas as formas de pagamento aceitas.",
        screenTitle: "viamenu.app/configuracoes/pagamento",
        screenSections: [
          { label: "💳 MercadoPago", highlight: true },
          { label: "   Access Token: ****" },
          { label: "   Public Key: ****" },
          { label: "📱 PIX — Ativado ✅" },
          { label: "💳 Cartão de Crédito — Ativado ✅" },
          { label: "💳 Cartão de Débito — Ativado ✅" },
          { label: "💵 Dinheiro — Ativado ✅" },
        ],
      },
      {
        title: "Integre o MercadoPago",
        description: "Para pagamentos online (PIX automático e cartão), insira suas credenciais do MercadoPago: Access Token e Public Key. As credenciais são encontradas no painel de desenvolvedores do MercadoPago.",
        screenTitle: "Configurar MercadoPago",
        screenSections: [
          { label: "🔑 Access Token: TEST-xxxx...", highlight: true },
          { label: "🔑 Public Key: TEST-xxxx..." },
          { label: "ℹ️ Obtenha em: mercadopago.com.br/developers" },
          { label: "✅ Salvar Credenciais", highlight: true },
        ],
        tip: "Use credenciais de teste (sandbox) para testar antes de ir para produção."
      },
    ],
  },
  {
    id: "clientes",
    icon: Users,
    category: "Clientes & Fidelidade",
    title: "Gestão de clientes e reativação",
    description: "Visualize o histórico de clientes, identifique inativos e crie cupons de reativação via WhatsApp.",
    difficulty: "Intermediário",
    duration: "8 min",
    steps: [
      {
        title: "Acesse a página de Clientes",
        description: "No menu lateral, clique em \"Clientes\". Veja a lista completa de clientes com pedidos realizados, total gasto e última visita.",
        screenTitle: "viamenu.app/clientes",
        screenSections: [
          { label: "🔍 Buscar cliente..." },
          { label: "👤 João Silva — 12 pedidos — R$ 580,00 — Há 3 dias" },
          { label: "👤 Maria Santos — 8 pedidos — R$ 420,00 — Há 1 semana" },
          { label: "👤 Pedro Oliveira — 2 pedidos — R$ 95,00 — Há 45 dias", highlight: true },
          { label: "📊 Total: 156 clientes cadastrados" },
        ],
      },
      {
        title: "Identifique clientes inativos",
        description: "A aba \"Reativação\" mostra clientes que não pedem há mais de 30 dias. O sistema sugere cupons personalizados para trazê-los de volta.",
        screenTitle: "Reativação de Clientes",
        screenSections: [
          { label: "⚠️ 12 clientes inativos há 30+ dias", highlight: true },
          { label: "👤 Pedro — último pedido há 45 dias" },
          { label: "   🎫 Cupom sugerido: 15% OFF" },
          { label: "   📱 Enviar via WhatsApp", highlight: true },
          { label: "👤 Lucia — último pedido há 60 dias" },
          { label: "   🎫 Cupom sugerido: 20% OFF" },
        ],
        tip: "Clientes reativados com cupons gastam em média 25% mais que o habitual na volta."
      },
    ],
  },
  {
    id: "fidelidade",
    icon: Star,
    category: "Clientes & Fidelidade",
    title: "Programa de fidelidade",
    description: "Crie programas de pontos, cartão fidelidade digital ou cashback para fidelizar seus clientes.",
    difficulty: "Intermediário",
    duration: "7 min",
    steps: [
      {
        title: "Acesse a página de Fidelidade",
        description: "No menu lateral, clique em \"Fidelidade\". Aqui você cria e gerencia seus programas de fidelização.",
        screenTitle: "viamenu.app/fidelidade",
        screenSections: [
          { label: "➕ Novo Programa", highlight: true },
          { label: "📊 Programas ativos: 1" },
          { label: "👥 Clientes participando: 45" },
          { label: "🎁 Recompensas resgatadas: 12" },
        ],
      },
      {
        title: "Escolha o tipo de programa",
        description: "Selecione entre três tipos: Pontos (acumule pontos a cada compra), Selos (cartão fidelidade digital) ou Cashback (dinheiro de volta).",
        screenTitle: "Novo Programa de Fidelidade",
        screenSections: [
          { label: "⭐ Pontos — Acumule pontos a cada R$ gasto", highlight: true },
          { label: "🏷️ Selos — Cartão fidelidade digital" },
          { label: "💰 Cashback — % de volta a cada compra" },
          { label: "📝 Nome do programa" },
          { label: "💵 Pedido mínimo: R$ 20,00" },
        ],
      },
      {
        title: "Configure as regras",
        description: "Para Pontos: defina pontos por real gasto e quantos pontos para resgatar. Para Selos: quantos selos para a recompensa. Para Cashback: porcentagem de retorno.",
        screenTitle: "Configuração do Programa",
        screenSections: [
          { label: "Tipo: Selos (Cartão Fidelidade)" },
          { label: "🏷️ Selos necessários: 10", highlight: true },
          { label: "🎁 Recompensa: Pizza grátis", highlight: true },
          { label: "📝 Descrição: A cada compra acima de R$ 20, ganhe um selo" },
          { label: "✅ Ativar Programa" },
        ],
        tip: "Programas de selos são os mais populares para restaurantes. Simples de entender e motivam compras recorrentes."
      },
    ],
  },
  {
    id: "estoque",
    icon: Package,
    category: "Gestão Operacional",
    title: "Controle de estoque e ingredientes",
    description: "Cadastre ingredientes, vincule a produtos e receba alertas quando o estoque estiver baixo.",
    difficulty: "Intermediário",
    duration: "8 min",
    steps: [
      {
        title: "Acesse a página de Estoque",
        description: "No menu lateral, clique em \"Estoque\". Veja todos os ingredientes cadastrados com quantidade atual, custo e status.",
        screenTitle: "viamenu.app/estoque",
        screenSections: [
          { label: "➕ Novo Ingrediente", highlight: true },
          { label: "🧀 Mussarela — 5.2 kg — R$ 35/kg — OK ✅" },
          { label: "🍅 Molho de tomate — 2.1 L — R$ 8/L — OK ✅" },
          { label: "⚠️ Farinha — 0.8 kg — R$ 5/kg — BAIXO!", highlight: true },
          { label: "📊 Movimentações recentes" },
        ],
      },
      {
        title: "Cadastre um ingrediente",
        description: "Clique em \"Novo Ingrediente\". Defina nome, unidade de medida, quantidade atual, custo por unidade e quantidade mínima para alerta.",
        screenTitle: "Novo Ingrediente",
        screenSections: [
          { label: "📝 Nome: Mussarela", highlight: true },
          { label: "📏 Unidade: kg" },
          { label: "📦 Quantidade: 5.2" },
          { label: "💰 Custo por unidade: R$ 35,00" },
          { label: "⚠️ Quantidade mínima: 2.0 kg", highlight: true },
        ],
      },
      {
        title: "Vincule ingredientes aos produtos",
        description: "Em cada produto do cardápio, vincule os ingredientes utilizados e a quantidade por unidade. O estoque é descontado automaticamente a cada pedido.",
        screenTitle: "Ingredientes do Produto",
        screenSections: [
          { label: "🍕 Pizza Margherita" },
          { label: "   🧀 Mussarela — 0.3 kg por unidade", highlight: true },
          { label: "   🍅 Molho — 0.15 L por unidade" },
          { label: "   🫓 Massa — 0.25 kg por unidade" },
          { label: "💰 Custo total: R$ 14,75 | Lucro: R$ 25,15 (63%)" },
        ],
        tip: "O relatório de custos mostra a margem de lucro de cada produto com base nos ingredientes. Use para otimizar preços."
      },
    ],
  },
  {
    id: "relatorios",
    icon: BarChart3,
    category: "Gestão Operacional",
    title: "Relatórios e análises",
    description: "Acesse relatórios de vendas, faturamento, produtos mais vendidos e tendências do seu negócio.",
    difficulty: "Avançado",
    duration: "6 min",
    steps: [
      {
        title: "Acesse Relatórios",
        description: "No menu lateral, clique em \"Relatórios\". Veja dados consolidados de vendas, pedidos e faturamento com gráficos interativos.",
        screenTitle: "viamenu.app/relatorios",
        screenSections: [
          { label: "📅 Período: Últimos 30 dias", highlight: true },
          { label: "📊 Faturamento: R$ 12.450,00" },
          { label: "📦 Total de pedidos: 287" },
          { label: "💰 Ticket médio: R$ 43,38" },
          { label: "📈 Gráfico de vendas por dia" },
          { label: "🏆 Top 5 produtos mais vendidos", highlight: true },
        ],
      },
      {
        title: "Relatório de custos",
        description: "Na página \"Relatório de Custos\", veja a margem de lucro de cada produto considerando os ingredientes cadastrados no estoque.",
        screenTitle: "viamenu.app/relatorio-custos",
        screenSections: [
          { label: "🍕 Pizza Margherita" },
          { label: "   Preço: R$ 39,90 | Custo: R$ 14,75 | Lucro: 63%" },
          { label: "🍔 Hambúrguer Especial" },
          { label: "   Preço: R$ 32,90 | Custo: R$ 11,20 | Lucro: 66%", highlight: true },
          { label: "📊 Margem média geral: 62%" },
        ],
      },
    ],
  },
  {
    id: "pdv",
    icon: LayoutDashboard,
    category: "Ponto de Venda",
    title: "Usando o PDV (Ponto de Venda)",
    description: "Registre vendas presenciais rápidas usando o PDV integrado, ideal para balcão e takeout.",
    difficulty: "Fácil",
    duration: "5 min",
    steps: [
      {
        title: "Acesse o PDV",
        description: "No menu lateral, clique em \"PDV\". A interface é otimizada para velocidade — selecione produtos, ajuste quantidades e finalize a venda.",
        screenTitle: "viamenu.app/pdv",
        screenSections: [
          { label: "📂 Categorias: Pizzas | Bebidas | Sobremesas", highlight: true },
          { label: "🍕 Pizza Marg. | 🍕 Pizza Calabr. | 🍕 Pizza Port." },
          { label: "🛒 Carrinho:" },
          { label: "   1x Pizza Margherita — R$ 39,90" },
          { label: "   2x Coca-Cola — R$ 19,90" },
          { label: "💰 Total: R$ 59,80", highlight: true },
          { label: "✅ Finalizar Venda", highlight: true },
        ],
      },
      {
        title: "Finalize a venda",
        description: "Selecione a forma de pagamento (PIX, cartão, dinheiro) e o tipo de pedido (balcão, mesa, viagem). O pedido é criado automaticamente no sistema.",
        screenTitle: "Finalizar Venda",
        screenSections: [
          { label: "📝 Tipo: Balcão | Mesa | Viagem" },
          { label: "💳 Pagamento: PIX | Crédito | Débito | Dinheiro", highlight: true },
          { label: "👤 Cliente (opcional): buscar..." },
          { label: "💰 Total: R$ 59,80" },
          { label: "✅ Confirmar e Imprimir", highlight: true },
        ],
      },
    ],
  },
  {
    id: "ifood",
    icon: Smartphone,
    category: "Integrações",
    title: "Integração com iFood",
    description: "Conecte seu iFood para sincronizar cardápio e receber pedidos diretamente no painel do ViaMenu.",
    difficulty: "Avançado",
    duration: "10 min",
    steps: [
      {
        title: "Acesse a página iFood",
        description: "No menu lateral, clique em \"iFood\". Insira suas credenciais de API do iFood (Client ID e Client Secret) obtidas no Portal do Parceiro.",
        screenTitle: "viamenu.app/ifood",
        screenSections: [
          { label: "🔗 Status: Desconectado", highlight: true },
          { label: "🔑 Client ID: ****" },
          { label: "🔑 Client Secret: ****" },
          { label: "🏪 Merchant ID: ****" },
          { label: "✅ Conectar ao iFood", highlight: true },
        ],
      },
      {
        title: "Sincronize o cardápio",
        description: "Após conectar, mapeie os itens do iFood para os produtos do seu cardápio ViaMenu. Isso garante que os pedidos entrem corretamente.",
        screenTitle: "Mapeamento iFood",
        screenSections: [
          { label: "🔄 Sincronizar Cardápio", highlight: true },
          { label: "🍕 iFood: Pizza Margherita → ViaMenu: Pizza Margherita ✅" },
          { label: "🍔 iFood: X-Burger → ViaMenu: (selecionar...)", highlight: true },
          { label: "📊 15 de 18 itens mapeados" },
        ],
      },
      {
        title: "Receba pedidos do iFood",
        description: "Os pedidos do iFood chegam automaticamente no seu painel de Pedidos e no Painel da Cozinha, junto com os pedidos do cardápio digital.",
        screenTitle: "Pedidos integrados",
        screenSections: [
          { label: "🟡 Pedido #143 — iFood 🔴 — R$ 55,00", highlight: true },
          { label: "🟡 Pedido #142 — Mesa 5 — R$ 67,80" },
          { label: "🟠 Pedido #141 — Delivery — R$ 45,00" },
          { label: "📊 Pedidos iFood hoje: 8 | Total: R$ 340,00" },
        ],
        tip: "Os pedidos do iFood são identificados com um badge vermelho para facilitar a diferenciação na cozinha."
      },
    ],
  },
  {
    id: "horarios",
    icon: Clock,
    category: "Configurações",
    title: "Horário de funcionamento",
    description: "Configure os dias e horários de funcionamento do seu restaurante. O cardápio mostra automaticamente se está aberto ou fechado.",
    difficulty: "Fácil",
    duration: "3 min",
    steps: [
      {
        title: "Acesse Configurações > Horário",
        description: "Defina o horário de abertura e fechamento para cada dia da semana. Você também pode desativar dias específicos.",
        screenTitle: "viamenu.app/configuracoes/horario",
        screenSections: [
          { label: "📅 Segunda: 11:00 — 23:00 ✅" },
          { label: "📅 Terça: 11:00 — 23:00 ✅" },
          { label: "📅 Quarta: Fechado ❌", highlight: true },
          { label: "📅 Quinta a Sábado: 11:00 — 00:00 ✅" },
          { label: "📅 Domingo: 11:00 — 22:00 ✅" },
          { label: "🔒 Fechar manualmente agora", highlight: true },
        ],
        tip: "Use o botão 'Fechar manualmente' para dias especiais ou emergências. O cardápio mostrará 'Fechado' para os clientes."
      },
    ],
  },
  {
    id: "endereco",
    icon: MapPin,
    category: "Configurações",
    title: "Endereço do restaurante",
    description: "Configure o endereço completo do seu restaurante para exibição no cardápio e cálculo de frete.",
    difficulty: "Fácil",
    duration: "2 min",
    steps: [
      {
        title: "Acesse Configurações > Endereço",
        description: "Preencha o endereço completo: CEP, rua, número, bairro, cidade e estado. O CEP preenche automaticamente os demais campos.",
        screenTitle: "viamenu.app/configuracoes/endereco",
        screenSections: [
          { label: "📮 CEP: 01001-000 (busca automática)", highlight: true },
          { label: "🏠 Rua: Praça da Sé" },
          { label: "🔢 Número: 100" },
          { label: "🏘️ Bairro: Sé" },
          { label: "🏙️ Cidade: São Paulo" },
          { label: "📍 Estado: SP" },
          { label: "✅ Salvar Endereço", highlight: true },
        ],
      },
    ],
  },
  {
    id: "popups",
    icon: Megaphone,
    category: "Marketing",
    title: "Pop-ups e promoções",
    description: "Crie pop-ups promocionais que aparecem quando o cliente abre seu cardápio digital.",
    difficulty: "Fácil",
    duration: "4 min",
    steps: [
      {
        title: "Acesse Configurações > Pop-ups",
        description: "Crie pop-ups com título, descrição, imagem e link opcional. Eles aparecem automaticamente quando o cliente abre o cardápio.",
        screenTitle: "viamenu.app/configuracoes/popups",
        screenSections: [
          { label: "➕ Novo Pop-up", highlight: true },
          { label: "🎉 Promoção de Terça — Ativo ✅" },
          { label: "   Pizza 2 por 1 às terças-feiras!" },
          { label: "🍺 Happy Hour — Inativo ❌" },
          { label: "🔀 Arrastar para reordenar" },
        ],
      },
      {
        title: "Configure o pop-up",
        description: "Defina o título, descrição, faça upload de uma imagem promocional e adicione um link (opcional). Ative/desative quando quiser.",
        screenTitle: "Editar Pop-up",
        screenSections: [
          { label: "📝 Título: Promoção de Terça", highlight: true },
          { label: "📝 Descrição: Pizza 2 por 1 nas terças!" },
          { label: "📷 Imagem promocional" },
          { label: "🔗 Link (opcional): /menu#pizzas" },
          { label: "✅ Exibir ao abrir cardápio", highlight: true },
          { label: "💾 Salvar" },
        ],
        tip: "Pop-ups com imagens têm 3x mais engajamento. Use fotos atrativas das suas promoções!"
      },
    ],
  },
  {
    id: "seguranca",
    icon: Shield,
    category: "Segurança",
    title: "Segurança e permissões",
    description: "Entenda o sistema de permissões, papéis de usuário e boas práticas de segurança da plataforma.",
    difficulty: "Avançado",
    duration: "5 min",
    steps: [
      {
        title: "Papéis de usuário",
        description: "O sistema possui diferentes papéis: Super Admin (plataforma), Admin do Restaurante (dono), Gerente, Atendente, Cozinha e Cliente. Cada um tem permissões específicas.",
        screenTitle: "Sistema de Permissões",
        screenSections: [
          { label: "👑 Super Admin — Acesso total à plataforma" },
          { label: "🏪 Admin Restaurante — Gerencia seu restaurante", highlight: true },
          { label: "📋 Gerente — Pedidos, clientes, relatórios" },
          { label: "🧑‍🍳 Cozinha — Apenas painel da cozinha" },
          { label: "👤 Cliente — Faz pedidos e acompanha" },
        ],
      },
      {
        title: "Boas práticas",
        description: "Use senhas fortes, não compartilhe credenciais e verifique regularmente os acessos. Todos os dados são protegidos com criptografia e RLS (Row Level Security).",
        screenTitle: "Segurança",
        screenSections: [
          { label: "🔒 Dados criptografados em trânsito e repouso" },
          { label: "🛡️ RLS — Isolamento de dados por restaurante", highlight: true },
          { label: "📧 Verificação de e-mail obrigatória" },
          { label: "🔑 Senhas com hash seguro (bcrypt)" },
          { label: "📝 Logs de auditoria para ações críticas", highlight: true },
        ],
      },
    ],
  },
  {
    id: "comandas-pdv",
    icon: LayoutDashboard,
    category: "Ponto de Venda",
    title: "Comandas digitais pelo PDV",
    description: "Abra comandas para mesas diretamente pelo PDV, envie itens incrementalmente e feche com múltiplas formas de pagamento.",
    difficulty: "Intermediário",
    duration: "6 min",
    steps: [
      {
        title: "Abra uma comanda pelo PDV",
        description: "No PDV, selecione o tipo de pedido \"Mesa\" e informe o número da mesa. Adicione itens ao carrinho e clique em \"Enviar p/ Comanda\".",
        screenTitle: "viamenu.app/pdv",
        screenSections: [
          { label: "📋 Tipo: Balcão | Mesa | Delivery", highlight: true },
          { label: "🔢 Nº da Mesa: 5" },
          { label: "🛒 Carrinho: 2 itens" },
          { label: "📤 Botão: Enviar p/ Comanda", highlight: true },
          { label: "✅ Itens enviados para cozinha + comanda" },
        ],
      },
      {
        title: "Adicione mais itens",
        description: "A comanda permanece aberta. Você pode adicionar novos itens a qualquer momento — cada envio cria uma nova sessão na comanda.",
        screenTitle: "Comanda Mesa 5 — Aberta",
        screenSections: [
          { label: "📋 Sessão 1 — 19:30 — 2 itens" },
          { label: "📋 Sessão 2 — 20:15 — 1 item", highlight: true },
          { label: "💰 Total acumulado: R$ 95,50" },
          { label: "➕ Adicionar mais itens" },
        ],
      },
      {
        title: "Feche a comanda na tela de Mesas",
        description: "Na página \"Mesas Ativas\", clique na mesa desejada e feche a comanda selecionando a forma de pagamento.",
        screenTitle: "Fechar Comanda — Mesa 5",
        screenSections: [
          { label: "💰 Total: R$ 95,50" },
          { label: "💳 Pagamento: PIX | Crédito | Débito | Dinheiro", highlight: true },
          { label: "✅ Confirmar Fechamento", highlight: true },
          { label: "🆓 Mesa liberada automaticamente" },
        ],
      },
    ],
  },
  {
    id: "financeiro",
    icon: DollarSign,
    category: "Gestão Operacional",
    title: "Módulo Financeiro completo",
    description: "Gerencie contas a pagar/receber, categorias financeiras, transações e fluxo de caixa do restaurante.",
    difficulty: "Avançado",
    duration: "8 min",
    steps: [
      {
        title: "Acesse o módulo Financeiro",
        description: "No menu lateral, clique em \"Financeiro\". Veja o resumo com receitas, despesas, saldo e gráficos do período.",
        screenTitle: "viamenu.app/financeiro",
        screenSections: [
          { label: "💰 Receitas: R$ 15.200,00" },
          { label: "💸 Despesas: R$ 8.400,00" },
          { label: "📊 Saldo: R$ 6.800,00", highlight: true },
          { label: "📈 Gráfico de fluxo de caixa" },
          { label: "📋 Últimas transações" },
        ],
      },
      {
        title: "Cadastre contas a pagar",
        description: "Na aba \"Contas\", cadastre despesas recorrentes como aluguel, fornecedores e energia. Defina categoria, valor e vencimento.",
        screenTitle: "Nova Conta a Pagar",
        screenSections: [
          { label: "📝 Descrição: Aluguel do ponto", highlight: true },
          { label: "💰 Valor: R$ 3.500,00" },
          { label: "📅 Vencimento: 10/04/2026" },
          { label: "📂 Categoria: Aluguel" },
          { label: "🔄 Recorrente: Mensal", highlight: true },
        ],
      },
      {
        title: "Categorize suas finanças",
        description: "Crie categorias personalizadas para receitas e despesas. O sistema inclui categorias padrão como Vendas, Aluguel e Fornecedores.",
        screenTitle: "Categorias Financeiras",
        screenSections: [
          { label: "💰 Receita: Vendas | Outros" },
          { label: "💸 Despesa: Aluguel | Fornecedores | Energia | Salários", highlight: true },
          { label: "➕ Nova Categoria" },
          { label: "🎨 Cor e ícone personalizáveis" },
        ],
      },
    ],
  },
  {
    id: "funcionarios",
    icon: UserCog,
    category: "Gestão Operacional",
    title: "Gestão de funcionários e ponto digital",
    description: "Cadastre funcionários, defina papéis e permissões, e controle o ponto digital com entrada/saída.",
    difficulty: "Intermediário",
    duration: "6 min",
    steps: [
      {
        title: "Cadastre funcionários",
        description: "Na página \"Funcionários\", adicione membros da equipe com nome, e-mail, telefone e papel (Gerente, Atendente, Cozinha).",
        screenTitle: "viamenu.app/funcionarios",
        screenSections: [
          { label: "➕ Novo Funcionário", highlight: true },
          { label: "👤 Carlos — Gerente — Ativo ✅" },
          { label: "👤 Ana — Atendente — Ativo ✅" },
          { label: "👤 Pedro — Cozinha — Ativo ✅" },
          { label: "📊 Total: 3 funcionários ativos" },
        ],
      },
      {
        title: "Controle de ponto digital",
        description: "Cada funcionário pode registrar entrada e saída via PIN. O sistema calcula automaticamente as horas trabalhadas.",
        screenTitle: "Ponto Digital",
        screenSections: [
          { label: "🔢 Digite seu PIN: ****", highlight: true },
          { label: "✅ Carlos — Entrada: 08:00" },
          { label: "✅ Carlos — Saída: 17:30" },
          { label: "⏱️ Total: 9h30min" },
          { label: "📊 Relatório mensal disponível", highlight: true },
        ],
      },
      {
        title: "Permissões por papel",
        description: "Cada papel tem permissões específicas. Gerentes acessam relatórios, atendentes o PDV e cozinha apenas o painel de produção.",
        screenTitle: "Permissões",
        screenSections: [
          { label: "📋 Gerente: Dashboard, Pedidos, Clientes, Relatórios" },
          { label: "🛒 Atendente: PDV, Pedidos, Mesas", highlight: true },
          { label: "🧑‍🍳 Cozinha: Painel da Cozinha" },
          { label: "🔒 Acesso restrito por papel" },
        ],
      },
    ],
  },
  {
    id: "marketing-ai",
    icon: Megaphone,
    category: "Marketing",
    title: "Marketing com IA",
    description: "Use inteligência artificial para gerar conteúdo de marketing, descrições de produtos e sugestões de combos.",
    difficulty: "Intermediário",
    duration: "5 min",
    steps: [
      {
        title: "Acesse o módulo de Marketing",
        description: "No menu lateral, clique em \"Marketing\". Gere conteúdo para redes sociais, e-mail marketing e promoções com IA.",
        screenTitle: "viamenu.app/marketing",
        screenSections: [
          { label: "🤖 Gerar conteúdo com IA", highlight: true },
          { label: "📱 Post para Instagram" },
          { label: "📧 E-mail promocional" },
          { label: "📝 Descrição de produto" },
          { label: "🎯 Sugestão de combo", highlight: true },
        ],
      },
      {
        title: "Gere posts para redes sociais",
        description: "Selecione o tipo de conteúdo, descreva a promoção e a IA gera textos prontos para copiar e postar.",
        screenTitle: "Gerador de Conteúdo",
        screenSections: [
          { label: "📝 Tipo: Post Instagram" },
          { label: "🎯 Tema: Promoção de terça-feira", highlight: true },
          { label: "🤖 Resultado gerado:" },
          { label: "   🔥 Terça é dia de pizza 2 por 1!..." },
          { label: "📋 Copiar | 🔄 Gerar outro", highlight: true },
        ],
        tip: "Use a IA para manter suas redes sociais sempre atualizadas com pouco esforço."
      },
    ],
  },
  {
    id: "avaliacoes",
    icon: Star,
    category: "Clientes & Fidelidade",
    title: "Avaliações dos clientes e entregadores",
    description: "Visualize avaliações de comida, atendimento e entrega que os clientes deixam após cada pedido.",
    difficulty: "Fácil",
    duration: "4 min",
    steps: [
      {
        title: "Avaliações automáticas após o pedido",
        description: "Quando o pedido é concluído, o cliente recebe automaticamente um formulário de avaliação com 3 categorias: Comida, Atendimento e Entrega (para delivery).",
        screenTitle: "viamenu.app/acompanhar?id=xxx",
        screenVariant: "mobile",
        screenSections: [
          { label: "⭐ Avalie seu pedido!" },
          { label: "🍔 Comida: ⭐⭐⭐⭐⭐", highlight: true },
          { label: "🏪 Restaurante: ⭐⭐⭐⭐☆" },
          { label: "🛵 Entrega: ⭐⭐⭐⭐⭐", highlight: true },
          { label: "💬 Comentário (opcional)" },
          { label: "✅ Enviar Avaliação" },
        ],
      },
      {
        title: "Veja as avaliações no Dashboard",
        description: "No Dashboard, a seção \"Avaliações dos Clientes\" mostra a nota geral, média por categoria (Comida, Atendimento, Entrega) e distribuição de estrelas.",
        screenTitle: "viamenu.app/dashboard",
        screenSections: [
          { label: "⭐ Nota geral: 4.6", highlight: true },
          { label: "🍽️ Comida: 4.8" },
          { label: "👋 Atendimento: 4.5" },
          { label: "🛵 Entrega: 4.4", highlight: true },
          { label: "📊 Distribuição: 5⭐ (60%) | 4⭐ (25%) | 3⭐ (10%)" },
          { label: "💬 Comentários recentes" },
        ],
        tip: "Acompanhe as avaliações de entrega para identificar entregadores que precisam melhorar o serviço."
      },
    ],
  },
  {
    id: "cupons",
    icon: Tag,
    category: "Marketing",
    title: "Cupons de desconto",
    description: "Crie cupons de desconto por porcentagem, valor fixo ou frete grátis para atrair e fidelizar clientes.",
    difficulty: "Intermediário",
    duration: "6 min",
    steps: [
      {
        title: "Acesse a página de Marketing",
        description: "No menu lateral, clique em \"Marketing\". Na seção de cupons, você pode criar, editar e gerenciar todos os cupons de desconto.",
        screenTitle: "viamenu.app/marketing",
        screenSections: [
          { label: "🎫 Cupons ativos: 3" },
          { label: "➕ Novo Cupom", highlight: true },
          { label: "🏷️ PRIMEIRACOMPRA — 15% OFF — 45 usos" },
          { label: "🏷️ VOLTESEMPRE — R$ 10 OFF — 12 usos" },
          { label: "🏷️ FRETEGRATIS — Frete grátis — 8 usos" },
        ],
      },
      {
        title: "Crie um novo cupom",
        description: "Defina o código, tipo de desconto (porcentagem, valor fixo ou frete grátis), valor mínimo do pedido, limite de usos e validade.",
        screenTitle: "Novo Cupom",
        screenSections: [
          { label: "🔤 Código: PROMO20", highlight: true },
          { label: "📊 Tipo: Porcentagem | Valor fixo | Frete grátis" },
          { label: "💰 Valor: 20%" },
          { label: "🛒 Pedido mínimo: R$ 50,00" },
          { label: "🔢 Máximo de usos: 100", highlight: true },
          { label: "📅 Válido até: 30/04/2026" },
        ],
        tip: "Cupons com data de validade curta criam urgência e geram mais conversões."
      },
    ],
  },
  {
    id: "popups",
    icon: Bell,
    category: "Marketing",
    title: "Popups promocionais no cardápio",
    description: "Configure popups que aparecem quando o cliente abre seu cardápio digital, ideais para divulgar promoções.",
    difficulty: "Fácil",
    duration: "3 min",
    steps: [
      {
        title: "Acesse Configurações > Popups",
        description: "No menu de configurações, clique em \"Popups e Promoções\". Crie banners promocionais que aparecem automaticamente ao abrir o cardápio.",
        screenTitle: "viamenu.app/configuracoes/popups",
        screenSections: [
          { label: "➕ Novo Popup", highlight: true },
          { label: "📣 Promoção de terça — Ativo ✅" },
          { label: "🎉 Happy Hour — Ativo ✅" },
          { label: "🔀 Arrastar para reordenar" },
        ],
      },
      {
        title: "Configure o popup",
        description: "Defina título, descrição, imagem opcional e link. Ative \"Mostrar ao abrir\" para exibir automaticamente quando o cliente acessa o cardápio.",
        screenTitle: "Novo Popup",
        screenSections: [
          { label: "📝 Título: Pizza 2 por 1 toda terça!", highlight: true },
          { label: "📝 Descrição: Aproveite toda terça-feira..." },
          { label: "📷 Imagem (opcional)" },
          { label: "🔗 Link (opcional)" },
          { label: "👁️ Mostrar ao abrir: ✅", highlight: true },
        ],
      },
    ],
  },
  {
    id: "caixa",
    icon: Receipt,
    category: "Gestão Operacional",
    title: "Caixa registradora e turnos",
    description: "Abra e feche turnos de caixa, registre sangrias e suprimentos, e controle o saldo do caixa.",
    difficulty: "Intermediário",
    duration: "7 min",
    steps: [
      {
        title: "Abra um turno de caixa",
        description: "No módulo Financeiro, aba \"Caixa\", clique em \"Abrir Caixa\". Informe o valor inicial (fundo de troco) para iniciar o turno.",
        screenTitle: "viamenu.app/financeiro",
        screenSections: [
          { label: "💰 Abrir Caixa", highlight: true },
          { label: "💵 Valor inicial: R$ 200,00" },
          { label: "👤 Operador: Carlos" },
          { label: "⏱️ Abertura: 08:00" },
          { label: "✅ Confirmar Abertura", highlight: true },
        ],
      },
      {
        title: "Registre movimentações",
        description: "Durante o turno, registre sangrias (retiradas) e suprimentos (adições). Cada venda é registrada automaticamente como entrada.",
        screenTitle: "Movimentações do Caixa",
        screenSections: [
          { label: "💰 Saldo atual: R$ 1.450,00", highlight: true },
          { label: "📥 Entrada — Pedido #142 — R$ 67,80" },
          { label: "📥 Entrada — Pedido #143 — R$ 45,00" },
          { label: "📤 Sangria — Pagamento fornecedor — R$ 200,00", highlight: true },
          { label: "📥 Suprimento — Troco extra — R$ 50,00" },
        ],
      },
      {
        title: "Feche o turno",
        description: "Ao final do expediente, feche o caixa. O sistema mostra o saldo esperado vs. real para conferência, e registra o resultado.",
        screenTitle: "Fechar Caixa",
        screenSections: [
          { label: "💰 Saldo esperado: R$ 1.450,00" },
          { label: "💵 Saldo informado: R$ 1.445,00", highlight: true },
          { label: "📊 Diferença: -R$ 5,00" },
          { label: "📝 Observações (opcional)" },
          { label: "✅ Confirmar Fechamento", highlight: true },
        ],
        tip: "Confira o caixa a cada troca de turno para identificar divergências rapidamente."
      },
    ],
  },
  {
    id: "sugestoes",
    icon: MessageSquare,
    category: "Plataforma",
    title: "Enviando sugestões e acompanhando melhorias",
    description: "Envie sugestões de melhorias, vote nas ideias da comunidade e acompanhe o status de cada solicitação.",
    difficulty: "Fácil",
    duration: "3 min",
    steps: [
      {
        title: "Acesse a página de Atualizações",
        description: "No menu lateral ou rodapé, clique em \"Changelog\". A aba \"Sugestões\" permite enviar ideias e ver o que outros usuários sugeriram.",
        screenTitle: "viamenu.app/changelog",
        screenSections: [
          { label: "📋 Aba: Histórico de Versões | Sugestões", highlight: true },
          { label: "💡 Enviar Nova Sugestão" },
          { label: "📝 Título da sugestão" },
          { label: "📂 Categoria: Cardápio | Pedidos | Delivery | ..." },
          { label: "✅ Enviar Sugestão", highlight: true },
        ],
      },
      {
        title: "Vote e acompanhe o status",
        description: "Veja as sugestões da comunidade, vote nas que você quer ver implementadas e acompanhe o status: Pendente → Aprovada → Desenvolvimento → Implementada.",
        screenTitle: "Sugestões da Comunidade",
        screenSections: [
          { label: "👍 12 votos — Modo escuro no cardápio — ✅ Aprovada", highlight: true },
          { label: "👍 8 votos — Integração com WhatsApp — 💻 Desenvolvimento" },
          { label: "👍 5 votos — Relatório por entregador — ⏳ Pendente" },
          { label: "💬 Resposta da equipe: 'Em breve!'", highlight: true },
        ],
        tip: "Sugestões com mais votos têm prioridade na implementação. Vote nas que mais importam para você!"
      },
    ],
  },
  {
    id: "analytics",
    icon: TrendingUp,
    category: "Configurações",
    title: "Google Analytics e Meta Pixel",
    description: "Integre ferramentas de analytics para rastrear visitas, conversões e campanhas no seu cardápio digital.",
    difficulty: "Avançado",
    duration: "5 min",
    steps: [
      {
        title: "Acesse Configurações > Analytics",
        description: "No menu de configurações, clique em \"Analytics\". Insira seu ID de medição do Google Analytics e/ou o ID do Meta Pixel.",
        screenTitle: "viamenu.app/configuracoes/analytics",
        screenSections: [
          { label: "📊 Google Analytics", highlight: true },
          { label: "   Measurement ID: G-XXXXXXXXXX" },
          { label: "📱 Meta Pixel (Facebook/Instagram)", highlight: true },
          { label: "   Pixel ID: 123456789" },
          { label: "✅ Salvar Configurações" },
        ],
      },
      {
        title: "Acompanhe métricas de tráfego",
        description: "Após configurar, o Google Analytics rastreia visitas ao cardápio, tempo de permanência e conversões. O Meta Pixel permite criar públicos para anúncios.",
        screenTitle: "Google Analytics Dashboard",
        screenSections: [
          { label: "👥 Visitantes hoje: 245" },
          { label: "📱 Mobile: 78% | Desktop: 22%" },
          { label: "📊 Taxa de conversão: 12%", highlight: true },
          { label: "🔗 Fontes: Instagram (40%) | Google (35%) | Direto (25%)" },
        ],
        tip: "Use os parâmetros UTM nos links compartilhados para saber exatamente de onde vêm seus clientes."
      },
    ],
  },
  {
    id: "redes-sociais",
    icon: Megaphone,
    category: "Configurações",
    title: "Redes sociais do restaurante",
    description: "Configure os links das redes sociais do seu restaurante para exibição no cardápio digital.",
    difficulty: "Fácil",
    duration: "2 min",
    steps: [
      {
        title: "Acesse Configurações > Redes Sociais",
        description: "No menu de configurações, clique em \"Redes Sociais\". Insira os links do Instagram, Facebook, TikTok e website do restaurante.",
        screenTitle: "viamenu.app/configuracoes/redes-sociais",
        screenSections: [
          { label: "📸 Instagram: @seurestaurante", highlight: true },
          { label: "📘 Facebook: facebook.com/seurestaurante" },
          { label: "🎵 TikTok: @seurestaurante" },
          { label: "🌐 Website: www.seurestaurante.com.br" },
          { label: "✅ Salvar", highlight: true },
        ],
     tip: "Os links aparecem como ícones no rodapé do seu cardápio digital, facilitando que os clientes sigam suas redes."
      },
    ],
  },
  {
    id: "whatsapp",
    icon: MessageCircle,
    category: "Integrações",
    title: "Conectando o WhatsApp do restaurante",
    description: "Conecte seu número comercial de WhatsApp para enviar notificações automáticas de pedidos e atender seus clientes.",
    difficulty: "Intermediário",
    duration: "8 min",
    steps: [
      {
        title: "Acesse Configurações > WhatsApp",
        description: "No menu lateral, vá em \"Configurações\" e clique na aba \"WhatsApp\". Aqui você gerencia a conexão do número e personaliza as notificações.",
        screenTitle: "viamenu.app/configuracoes/whatsapp",
        screenSections: [
          { label: "⚠️ Use um número comercial exclusivo", highlight: true },
          { label: "📱 Status: Desconectado" },
          { label: "🔗 Botão: Conectar WhatsApp", highlight: true },
          { label: "🔔 Aba: Notificações" },
          { label: "📝 Aba: Templates" },
        ],
      },
      {
        title: "Crie a instância e escaneie o QR Code",
        description: "Clique em \"Conectar WhatsApp\". O sistema gerará um QR Code na tela. Abra o WhatsApp no celular, vá em Aparelhos Conectados e escaneie o código.",
        screenTitle: "Conectar WhatsApp",
        screenSections: [
          { label: "📱 QR Code exibido na tela", highlight: true },
          { label: "1️⃣ Abra o WhatsApp no celular" },
          { label: "2️⃣ Vá em Configurações > Aparelhos Conectados" },
          { label: "3️⃣ Toque em 'Conectar um aparelho'" },
          { label: "4️⃣ Escaneie o QR Code acima", highlight: true },
        ],
        tip: "Use um número comercial exclusivo para o restaurante. Evite usar seu número pessoal para não misturar conversas."
      },
      {
        title: "Confirme a conexão",
        description: "Após escanear, aguarde alguns segundos. O status mudará para \"Conectado\" com o número do telefone exibido. A conexão ficará ativa 24/7.",
        screenTitle: "WhatsApp Conectado",
        screenSections: [
          { label: "✅ Status: Conectado", highlight: true },
          { label: "📱 Número: +55 (11) 99999-0000" },
          { label: "🟢 Online desde: 10/03/2026 14:30" },
          { label: "🔄 Reconexão automática ativada" },
        ],
      },
      {
        title: "Configure as notificações automáticas",
        description: "Na aba \"Notificações\", ative os gatilhos desejados: novo pedido recebido, pedido em preparo, pedido pronto, saiu para entrega e pedido entregue.",
        screenTitle: "Notificações WhatsApp",
        screenSections: [
          { label: "🔔 Novo pedido recebido — ✅ Ativo", highlight: true },
          { label: "👨‍🍳 Pedido em preparo — ✅ Ativo" },
          { label: "✅ Pedido pronto — ✅ Ativo" },
          { label: "🏍️ Saiu para entrega — ✅ Ativo" },
          { label: "📦 Pedido entregue — ✅ Ativo" },
          { label: "❌ Pedido cancelado — ❌ Inativo" },
        ],
        tip: "Recomendamos ativar pelo menos 'Pedido confirmado' e 'Pedido pronto' — são as notificações mais valorizadas pelos clientes."
      },
      {
        title: "Personalize os templates de mensagem",
        description: "Na aba \"Templates\", customize as mensagens enviadas em cada status. Use variáveis como {{customer_name}}, {{order_id}} e {{total}} para personalizar.",
        screenTitle: "Templates de Mensagem",
        screenSections: [
          { label: "📝 Template: Pedido Confirmado", highlight: true },
          { label: "   Olá {{customer_name}}! 🎉" },
          { label: "   Seu pedido #{{order_id}} foi confirmado!" },
          { label: "   Total: R$ {{total}}" },
          { label: "📝 Template: Pedido Pronto", highlight: true },
          { label: "   {{customer_name}}, seu pedido está pronto! 🍕" },
          { label: "🔄 Restaurar template padrão" },
        ],
        tip: "Use emojis nas mensagens para deixá-las mais amigáveis. Mensagens personalizadas aumentam a satisfação do cliente."
      },
    ],
  },
  {
    id: "instagram-shop",
    icon: Megaphone,
    category: "Integrações",
    title: "Integração com Instagram Shop",
    description: "Sincronize seu cardápio com o catálogo do Instagram Shopping e venda diretamente pela rede social.",
    difficulty: "Avançado",
    duration: "10 min",
    steps: [
      {
        title: "Crie um App no Meta for Developers",
        description: "Acesse developers.facebook.com, clique em \"Criar App\" e selecione o tipo \"Business\". Nomeie como quiser (ex.: \"MeuRestaurante Shop\") e finalize.",
        screenTitle: "developers.facebook.com",
        screenSections: [
          { label: "➕ Botão: Criar App" },
          { label: "🏢 Tipo: Business", highlight: true },
          { label: "📝 Nome do App" },
          { label: "✅ Botão: Criar" },
        ],
      },
      {
        title: "Copie o App ID e App Secret",
        description: "No painel do App, vá em Configurações > Básico. Copie o \"ID do Aplicativo\" (App ID) e o \"Chave Secreta do Aplicativo\" (App Secret).",
        screenTitle: "Configurações > Básico",
        screenSections: [
          { label: "🔑 ID do Aplicativo: 123456789...", highlight: true },
          { label: "🔒 Chave Secreta: ••••••••", highlight: true },
          { label: "📧 E-mail de contato" },
          { label: "🌐 Domínios do app" },
        ],
      },
      {
        title: "Gere o Access Token no Graph API Explorer",
        description: "Acesse developers.facebook.com/tools/explorer, selecione seu App no dropdown, marque a permissão \"catalog_management\" e clique em \"Generate Access Token\".",
        screenTitle: "Graph API Explorer",
        screenSections: [
          { label: "📱 Selecione seu App", highlight: true },
          { label: "🔐 Permissões:" },
          { label: "   ✅ catalog_management", highlight: true },
          { label: "🔑 Botão: Generate Access Token", highlight: true },
          { label: "📋 Token gerado (copie!)" },
        ],
        tip: "Este token inicial dura apenas ~1 hora. No próximo passo você trocará por um de longa duração (~60 dias)."
      },
      {
        title: "Troque por um token de longa duração",
        description: "Cole na barra do navegador a URL abaixo, substituindo os valores. O JSON retornado conterá o access_token de longa duração (~60 dias):\n\nhttps://graph.facebook.com/v19.0/oauth/access_token?grant_type=fb_exchange_token&client_id=SEU_APP_ID&client_secret=SEU_APP_SECRET&fb_exchange_token=TOKEN_CURTO",
        screenTitle: "Navegador",
        screenSections: [
          { label: "🔗 Cole a URL com seus dados" },
          { label: "📋 Resposta JSON:" },
          { label: '   "access_token": "EAAxxxx..."', highlight: true },
          { label: '   "token_type": "bearer"' },
          { label: '   "expires_in": 5184000 (60 dias)' },
        ],
        tip: "⚠️ O Access Token expira a cada 60 dias. Agende um lembrete para renová-lo acessando a página de integração no painel."
      },
      {
        title: "Crie ou selecione um catálogo no Commerce Manager",
        description: "Acesse business.facebook.com/commerce, crie um catálogo ou selecione um existente. O Catalog ID aparece na URL da página (ex.: /catalog/1234567890).",
        screenTitle: "Commerce Manager",
        screenSections: [
          { label: "📦 Meus Catálogos" },
          { label: "   Catálogo do Restaurante", highlight: true },
          { label: "🔗 URL: /catalog/1234567890" },
          { label: "📋 Copie o número do Catalog ID", highlight: true },
        ],
      },
      {
        title: "Cole as credenciais no Via Menu",
        description: "No painel do Via Menu, acesse Integrações > Instagram Shop. Cole o App ID, App Secret, Access Token e Catalog ID nos campos. Ative a integração.",
        screenTitle: "viamenu.com.br/instagram-shop",
        screenSections: [
          { label: "🔑 Campo: App ID", highlight: true },
          { label: "🔒 Campo: App Secret", highlight: true },
          { label: "🔐 Campo: Access Token", highlight: true },
          { label: "📦 Campo: Catalog ID", highlight: true },
          { label: "✅ Switch: Integração ativa" },
          { label: "💾 Botão: Salvar Credenciais" },
        ],
      },
      {
        title: "Sincronize seus produtos",
        description: "Na aba \"Catálogo\", selecione os produtos que deseja enviar ao Instagram Shop e clique em \"Sincronizar\". Os produtos com imagem válida serão enviados ao catálogo do Meta.",
        screenTitle: "Instagram Shop — Catálogo",
        screenSections: [
          { label: "☑️ Selecionar todos os produtos" },
          { label: "🍔 Smash Burger — R$ 28,90 ✅", highlight: true },
          { label: "🍟 Batata Frita — R$ 18,90 ✅", highlight: true },
          { label: "🥤 Coca-Cola — R$ 6,90 ✅" },
          { label: "📤 Botão: Sincronizar (3)", highlight: true },
        ],
        tip: "Certifique-se de que seus produtos tenham imagens com URL pública. Imagens locais ou sem URL não serão aceitas pela Meta."
      },
    ],
  },
];

const difficultyColor = {
  "Fácil": "bg-green-500/20 text-green-400 border-green-500/30",
  "Intermediário": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "Avançado": "bg-red-500/20 text-red-400 border-red-500/30",
};

const Tutoriais = () => {
  const [search, setSearch] = useState("");
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [...new Set(tutorials.map(t => t.category))];

  const filtered = tutorials.filter(t => {
    const matchesSearch = !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase()) ||
      t.category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedTutorial) {
    const step = selectedTutorial.steps[currentStep];
    const total = selectedTutorial.steps.length;

    return (
      <div className="min-h-screen bg-background">
        <section className="pt-32 pb-24">
          <div className="container max-w-5xl">
            <button
              onClick={() => { setSelectedTutorial(null); setCurrentStep(0); }}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar aos tutoriais
            </button>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-3 mb-2">
                <selectedTutorial.icon className="h-6 w-6 text-primary" />
                <Badge variant="outline" className={difficultyColor[selectedTutorial.difficulty]}>
                  {selectedTutorial.difficulty}
                </Badge>
                <span className="text-xs text-muted-foreground">⏱️ {selectedTutorial.duration}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{selectedTutorial.title}</h1>
              <p className="text-muted-foreground mb-8">{selectedTutorial.description}</p>

              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {selectedTutorial.steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={`h-2 rounded-full transition-all ${i === currentStep ? "w-8 bg-primary" : i < currentStep ? "w-4 bg-primary/50" : "w-4 bg-muted"}`}
                  />
                ))}
                <span className="text-xs text-muted-foreground ml-2">Passo {currentStep + 1} de {total}</span>
              </div>

              {/* Step content */}
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="grid md:grid-cols-2 gap-8"
              >
                <div>
                  <h2 className="text-xl font-bold mb-3">
                    <span className="text-primary mr-2">{currentStep + 1}.</span>
                    {step.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">{step.description}</p>

                  {step.tip && (
                    <div className="rounded-xl bg-primary/10 border border-primary/20 p-4 mb-6">
                      <p className="text-sm"><span className="font-semibold text-primary">💡 Dica: </span>{step.tip}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentStep === 0}
                      onClick={() => setCurrentStep(c => c - 1)}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                    </Button>
                    {currentStep < total - 1 ? (
                      <Button size="sm" onClick={() => setCurrentStep(c => c + 1)} className="bg-gradient-primary text-primary-foreground">
                        Próximo <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => { setSelectedTutorial(null); setCurrentStep(0); }} className="bg-gradient-primary text-primary-foreground">
                        ✅ Concluir Tutorial
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <ScreenMockup title={step.screenTitle} variant={step.screenVariant}>
                    <ScreenPlaceholder sections={step.screenSections} />
                  </ScreenMockup>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-24">
        <div className="container max-w-5xl">
          <Link to="/ajuda" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar à Central de Ajuda
          </Link>

          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Tutoriais</h1>
            <p className="text-lg text-muted-foreground mb-8">Guias passo a passo com prints de tela para todas as funcionalidades.</p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Buscar tutorial..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-muted border-border"
              />
            </div>
          </motion.div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className={!selectedCategory ? "bg-gradient-primary text-primary-foreground" : ""}
            >
              Todos ({tutorials.length})
            </Button>
            {categories.map(cat => {
              const count = tutorials.filter(t => t.category === cat).length;
              return (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                  className={selectedCategory === cat ? "bg-gradient-primary text-primary-foreground" : ""}
                >
                  {cat} ({count})
                </Button>
              );
            })}
          </div>

          {/* Tutorial grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((tutorial, i) => (
              <motion.button
                key={tutorial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => { setSelectedTutorial(tutorial); setCurrentStep(0); }}
                className="text-left rounded-xl border border-border bg-gradient-card p-5 hover:border-primary/40 hover:shadow-glow transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <tutorial.icon className="h-5 w-5 text-primary" />
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${difficultyColor[tutorial.difficulty]}`}>
                    {tutorial.difficulty}
                  </Badge>
                </div>
                <p className="text-[10px] text-primary font-medium mb-1">{tutorial.category}</p>
                <h3 className="font-semibold text-sm mb-1.5 group-hover:text-primary transition-colors">{tutorial.title}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{tutorial.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">⏱️ {tutorial.duration} • {tutorial.steps.length} passos</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </motion.button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum tutorial encontrado</h3>
              <p className="text-muted-foreground text-sm">Tente buscar por outro termo.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tutoriais;
